import { useEffect, useState } from 'react';
import { type LucideIcon, Menu, Plus, Trash2Icon } from 'lucide-react';
import { type UseMutationResult } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { useAuth0 } from '@auth0/auth0-react';
import { MenuCheckboxItem, MenuProvider, MenuSeparator, MenuSub, dropdownPrimitives } from '@/components/menu-renderer';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTriggerButton } from '@/components/ui/dropdown-menu';
import { SortByValueMenu, SortOrderMenu } from '@/components/menu-items/node-menu-items';
import { type Features, useFeatureStore } from '@/hooks/data/feature/useFeatureStore';
import { ToolbarFilterButton } from '@/components/toolbars/toolbar-filter-button';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { ToolbarButton } from '@/components/toolbars/toolbar-button';
import { useTableStore } from '@/hooks/data/datatable/useTableStore';
import { useOverflowCollapse } from '@/hooks/useOverflowCollapse';
import { DataTable } from '@/components/data-table/data-table';
import { TruncatedText } from '@/components/ui/truncated-text';
import { useDataTablePagination } from '@/hooks/usePagination';
import { DropdownSelect } from '@/components/dropdown-select';
import { FilterBar } from '@/components/toolbars/filter-bar';
import { defaultColumnsSettings } from '@/types/datatable';
import { EmptyLayout } from '@/components/empty-layout';
import { Toolbar } from '@/components/toolbars/toolbar';
import { useSearchQuery } from '@/hooks/useSearchQuery';
import { Separator } from '@/components/ui/separator';
import { getCorrectPageNumber } from '@/lib/utils';
import { type EntityType } from '@/types/entity';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';

type ManagementAction = {
  icon?: LucideIcon;
  iconClassName?: string;
  label?: string;
  disabled?: boolean;
  tooltip?: string;
};

type ListHookParams = {
  pagination: { offset: number; limit: number };
  sort: { order: string; value: string };
  queryString: string;
};

type ManagementListResult<T> = {
  data: Array<T>;
  pagination?: { count: number; limit: number; offset: number };
  isFetching: boolean;
  isPending: boolean;
} & Record<string, unknown>;

type ManagementEmptyState = {
  icon: LucideIcon;
  title: string;
  message: string;
  buttonText: string;
  onClick?: () => void;
  disabled?: boolean;
  disabledMessage?: string;
};

type ManagementLayoutProps<T> = {
  featureFlag?: keyof Features;
  tableEntity: EntityType;
  columns: Array<ColumnDef<T>>;
  listHook: (params: ListHookParams, opts: object) => ManagementListResult<T>;
  createAction:
    | (ManagementAction & { onClick?: () => void })
    | ((listResult: ManagementListResult<T>) => ManagementAction & { onClick?: () => void });
  deleteAction?: ManagementAction & {
    mutation: UseMutationResult<any, Error, any>;
  };
  emptyState: ManagementEmptyState | ((listResult: ManagementListResult<T>) => ManagementEmptyState);
  intrinsicTableWidth?: boolean;
};

export const ManagementLayout = <T,>({
  featureFlag,
  tableEntity,
  columns,
  listHook,
  createAction,
  deleteAction,
  emptyState,
  intrinsicTableWidth = false,
}: ManagementLayoutProps<T>) => {
  const [pageReady, setPageReady] = useState<boolean>(false);

  const { replace } = useRouter();
  const { selectedEntity, setSelectedEntity, setSelectedEntityType } = useTableStore();
  const { queryString, debouncedSearch, showFilterInput, setShowFilter } = useSearchQuery(tableEntity);
  const { ref: toolbarRef, collapsed } = useOverflowCollapse<HTMLDivElement>();
  const { offset, limit, pageIndex, sortOrder, sortValue, setQueryParams } = useDataTablePagination(tableEntity);
  const { isAuthenticated } = useAuth0();

  const tCommon = useTranslations('common');
  const tDialogs = useTranslations('dialogs');
  const setPageIndex = useTableStore((s) => s.setPageIndex);
  const openModal = useOpenModal();

  const listResult = listHook(
    {
      pagination: {
        offset,
        limit,
      },
      sort: {
        order: sortOrder,
        value: sortValue,
      },

      queryString,
    },
    {
      enabled: isAuthenticated,
    },
  );
  const { data = [], isFetching, isPending, pagination } = listResult;
  const resolvedCreateAction = typeof createAction === 'function' ? createAction(listResult) : createAction;
  const resolvedEmptyState = typeof emptyState === 'function' ? emptyState(listResult) : emptyState;

  // If a feature flag is not specified, assume the feature is enabled, otherwise check the state.
  const managementSectionEnabled = useFeatureStore((state) => (featureFlag ? state[featureFlag] : true));

  // If the feature flag is disabled, redirect the user back to /browse
  useEffect(() => {
    if (!managementSectionEnabled) {
      void replace('/browse');
    }
  }, [managementSectionEnabled, replace]);

  useEffect(() => {
    if (!isFetching && pagination) {
      const pageNumber = getCorrectPageNumber(pagination, setPageReady);
      setQueryParams({
        pageIndex: pageNumber + 1,
        pageSize: pagination.limit,
        sortValue,
        sortOrder,
        queryString,
      });
      setPageIndex(tableEntity, pageNumber);
    }
  }, [isFetching, pagination]);

  return (
    // If the feature flag is disabled, don't render anything
    managementSectionEnabled && (
      <div className="flex h-full flex-col space-y-px">
        <Toolbar ref={toolbarRef} className="justify-between border-t border-t-toolbar-bevel" isUpload={false}>
          <div className="ml-2 flex grow items-center gap-x-2">
            {resolvedCreateAction && (
              <ToolbarButton
                onClick={resolvedCreateAction.onClick}
                label={resolvedCreateAction.label ?? `Create ${tableEntity}`}
                icon={resolvedCreateAction.icon ?? Plus}
                disabled={resolvedCreateAction.disabled}
                tooltip={resolvedCreateAction.tooltip}
                iconClassName={resolvedCreateAction.iconClassName}
              />
            )}
            {selectedEntity && deleteAction && !deleteAction.disabled && (
              <ToolbarButton
                onClick={() => {
                  openModal(ModalId.DeleteConfirmationAlertDialog, {
                    'content': (
                      <div className="flex min-w-0 flex-wrap items-baseline gap-x-1">
                        <span className="shrink-0">{tDialogs('deleteConfirmationPrefix')}</span>
                        <TruncatedText text={String(selectedEntity.getValue('name'))} className="min-w-0 font-bold" />
                        <span className="shrink-0">{tableEntity.toLowerCase()}?</span>
                      </div>
                    ),
                    'onClick': () => {
                      if (selectedEntity.getValue('id')) {
                        deleteAction.mutation.mutate({
                          id: selectedEntity.getValue('id'),
                          name: selectedEntity.getValue('name'),
                          onConfirm: () => setSelectedEntity(null),
                        });
                      }
                    },
                    'data-cy': tableEntity.toLowerCase(),
                  });
                }}
                label={deleteAction.label ?? `Delete ${tableEntity}`}
                icon={deleteAction.icon ?? Trash2Icon}
              />
            )}
          </div>
          <div className="flex items-center">
            <Separator orientation="vertical" className="h-6 bg-ui-light" />
            {collapsed ? (
              <DropdownMenu>
                <DropdownMenuTriggerButton
                  variant="main-toolbar"
                  size="main-toolbar"
                  icon={Menu}
                  data-cy="toolbar-dropdown-menu-button"
                  tooltip={tCommon('moreOptions')}
                  className="mx-1"
                />
                <DropdownMenuContent>
                  <MenuProvider primitives={dropdownPrimitives}>
                    <MenuCheckboxItem
                      checked={showFilterInput}
                      disabled={queryString === '' && data.length === 0}
                      onCheckedChange={(checked) => setShowFilter?.(checked)}
                    >
                      {tCommon('showFilter')}
                    </MenuCheckboxItem>
                    <MenuSeparator />

                    <MenuSub label={tCommon('sort')}>
                      <SortByValueMenu tableEntity={tableEntity} />
                      <MenuSeparator />
                      <SortOrderMenu tableEntity={tableEntity} />
                    </MenuSub>
                  </MenuProvider>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex flex-row items-center">
                <ToolbarFilterButton
                  disabled={queryString === '' && data.length === 0}
                  showFilterInput={showFilterInput}
                  onClick={() => setShowFilter?.(!showFilterInput)}
                />
                <Separator orientation="vertical" className="h-6 bg-ui-light" />
                <DropdownSelect title={tCommon('sort')} menuClassName="w-54">
                  <SortByValueMenu tableEntity={tableEntity} />
                  <MenuSeparator />
                  <SortOrderMenu tableEntity={tableEntity} />
                </DropdownSelect>
              </div>
            )}
            <Separator orientation="vertical" className="h-6 bg-ui-light" />
          </div>
        </Toolbar>
        {showFilterInput && debouncedSearch && (
          <FilterBar showFilterInput={showFilterInput} queryString={queryString} onChange={debouncedSearch} />
        )}
        {!isPending && pageReady && !isFetching && !data.length && queryString === '' ? (
          <EmptyLayout {...resolvedEmptyState} />
        ) : (
          <DataTable
            tableEntity={tableEntity}
            tableSettings={defaultColumnsSettings[tableEntity]}
            data={data}
            columns={columns}
            pagination={{ pageIndex, pageSize: limit }}
            rowCount={pagination?.count}
            entity={tableEntity}
            setSelectedEntity={setSelectedEntity}
            setSelectedEntityType={setSelectedEntityType}
            intrinsicWidth={intrinsicTableWidth}
            isLoading={isPending || !pageReady}
          />
        )}
      </div>
    )
  );
};
